import { Client } from "@microsoft/microsoft-graph-client";
import { getAccessToken } from "./authService";
import { sharePointConfig } from "../config/authConfig";

// Crear cliente de Graph con autenticación
const getGraphClient = async () => {
    const accessToken = await getAccessToken();
    
    return Client.init({
        authProvider: (done) => {
            done(null, accessToken);
        }
    });
};

// Variables para almacenar IDs (se obtienen dinámicamente)
let cachedSiteId = null;
let cachedDriveId = null;

// Obtener Site ID
export const getSiteId = async () => {
    if (cachedSiteId) return cachedSiteId;
    
    try {
        const client = await getGraphClient();
        const site = await client
            .api(sharePointConfig.getSiteApiUrl())
            .get();
        
        cachedSiteId = site.id;
        console.log("Site ID obtenido:", cachedSiteId);
        return cachedSiteId;
    } catch (error) {
        console.error("Error obteniendo Site ID:", error);
        throw error;
    }
};

// Obtener Drive ID
export const getDriveId = async () => {
    if (cachedDriveId) return cachedDriveId;
    
    try {
        const siteId = await getSiteId();
        const client = await getGraphClient();
        
        const drives = await client
            .api(`/sites/${siteId}/drives`)
            .get();
        
        console.log("Drives disponibles:", drives.value.map(d => d.name));
        
        // Buscar el drive principal (el primero suele ser el correcto)
        // Intentar varios nombres posibles
        const possibleNames = [
            sharePointConfig.libraryName,
            "Documents",
            "Documentos compartidos",
            "Shared Documents",
            "Documentos"
        ];
        
        let documentsDrive = drives.value.find(drive => 
            possibleNames.some(name => 
                drive.name.toLowerCase() === name.toLowerCase()
            )
        );
        
        // Si no encuentra por nombre, usar el primer drive (generalmente es el correcto)
        if (!documentsDrive && drives.value.length > 0) {
            documentsDrive = drives.value[0];
            console.log("Usando el primer drive disponible:", documentsDrive.name);
        }
        
        if (!documentsDrive) {
            throw new Error("No se encontró ninguna biblioteca de documentos");
        }
        
        cachedDriveId = documentsDrive.id;
        console.log("Drive ID obtenido:", cachedDriveId, "Drive name:", documentsDrive.name);
        return cachedDriveId;
    } catch (error) {
        console.error("Error obteniendo Drive ID:", error);
        throw error;
    }
};

// Listar items en una carpeta
export const listItems = async (folderPath = "/") => {
    try {
        const siteId = await getSiteId();
        const driveId = await getDriveId();
        const client = await getGraphClient();
        
        let apiUrl;
        if (folderPath === "/" || folderPath === "") {
            // Raíz de la biblioteca
            apiUrl = `/sites/${siteId}/drives/${driveId}/root/children`;
        } else {
            // Carpeta específica
            const encodedPath = encodeURIComponent(folderPath);
            apiUrl = `/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:/children`;
        }
        
        const result = await client
            .api(apiUrl)
            .select('id,name,size,lastModifiedDateTime,folder,file,lastModifiedBy')
            .get();
        
        return result.value || [];
    } catch (error) {
        console.error("Error listando items:", error);
        // Si la carpeta no existe, retornar array vacío
        if (error.statusCode === 404) {
            return [];
        }
        throw error;
    }
};

// Descargar archivo
export const downloadFile = async (itemId, fileName) => {
    try {
        const siteId = await getSiteId();
        const driveId = await getDriveId();
        const client = await getGraphClient();
        
        // Obtener URL de descarga
        const item = await client
            .api(`/sites/${siteId}/drives/${driveId}/items/${itemId}`)
            .select('@microsoft.graph.downloadUrl')
            .get();
        
        const downloadUrl = item['@microsoft.graph.downloadUrl'];
        
        // Descargar el archivo
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        
        // Crear link de descarga
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error("Error descargando archivo:", error);
        throw error;
    }
};

// Subir archivo (archivos pequeños < 4MB)
export const uploadFile = async (file, filePath) => {
    try {
        const siteId = await getSiteId();
        const driveId = await getDriveId();
        const client = await getGraphClient();
        
        // Dividir la ruta en partes y codificar solo el nombre del archivo
        const pathParts = filePath.split('/');
        const fileName = pathParts.pop(); // Obtener el nombre del archivo
        const folderPath = pathParts.join('/'); // Obtener la ruta de la carpeta
        
        const encodedFileName = encodeURIComponent(fileName);
        const fullPath = folderPath ? `${folderPath}/${encodedFileName}` : encodedFileName;
        
        console.log('Subiendo archivo a:', fullPath);
        
        const result = await client
            .api(`/sites/${siteId}/drives/${driveId}/root:/${fullPath}:/content`)
            .put(file);
        
        console.log("Archivo subido exitosamente:", result);
        return result;
    } catch (error) {
        console.error("Error subiendo archivo:", error);
        throw error;
    }
};
// Subir archivo grande (> 4MB) con upload session
export const uploadLargeFile = async (folderPath, file) => {
    try {
        const siteId = await getSiteId();
        const driveId = await getDriveId();
        const client = await getGraphClient();
        
        const fileName = file.name;
        const encodedPath = encodeURIComponent(`${folderPath}/${fileName}`);
        
        // Crear sesión de subida
        const uploadSession = await client
            .api(`/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:/createUploadSession`)
            .post({
                item: {
                    "@microsoft.graph.conflictBehavior": "rename",
                    name: fileName
                }
            });
        
        const uploadUrl = uploadSession.uploadUrl;
        const fileSize = file.size;
        const chunkSize = 327680; // 320 KB
        let start = 0;
        
        while (start < fileSize) {
            const end = Math.min(start + chunkSize, fileSize);
            const chunk = file.slice(start, end);
            
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Range': `bytes ${start}-${end - 1}/${fileSize}`,
                    'Content-Length': chunk.size.toString()
                },
                body: chunk
            });
            
            if (!response.ok && response.status !== 202) {
                throw new Error(`Error subiendo chunk: ${response.statusText}`);
            }
            
            start = end;
        }
        
        console.log("Archivo grande subido exitosamente");
        return true;
    } catch (error) {
        console.error("Error subiendo archivo grande:", error);
        throw error;
    }
};

// Crear carpeta
export const createFolder = async (parentPath, folderName) => {
    try {
        const siteId = await getSiteId();
        const driveId = await getDriveId();
        const client = await getGraphClient();
        
        let apiUrl;
        if (parentPath === "/" || parentPath === "") {
            apiUrl = `/sites/${siteId}/drives/${driveId}/root/children`;
        } else {
            const encodedPath = encodeURIComponent(parentPath);
            apiUrl = `/sites/${siteId}/drives/${driveId}/root:/${encodedPath}:/children`;
        }
        
        const result = await client
            .api(apiUrl)
            .post({
                name: folderName,
                folder: {},
                "@microsoft.graph.conflictBehavior": "rename"
            });
        
        console.log("Carpeta creada:", result);
        return result;
    } catch (error) {
        console.error("Error creando carpeta:", error);
        throw error;
    }
};

// Función auxiliar para construir la ruta completa
export const buildPath = (pathArray) => {
    if (!pathArray || pathArray.length === 0) return "/";
    return pathArray.join('/');
};
